import React, { memo, useState } from 'react';
import { GlobalSidebar } from './GlobalSidebar';
import { CollapseButton } from './CollapseButton';
import { useLayout } from '@/contexts/LayoutContext';
import { LowCreditBanner, BuyCreditModal } from '@/components/credits';

interface AppLayoutProps {
  children: React.ReactNode;
  showSidebar?: boolean;
}

/**
 * AppLayout - Composant de layout principal avec CSS Grid et optimisations de performance
 * 
 * Ce composant implémente le système CSS Grid pour gérer automatiquement
 * le positionnement de la sidebar et du contenu principal. Il élimine
 * le besoin de calculs manuels de marges dans les pages individuelles.
 * 
 * Fonctionnalités:
 * - CSS Grid avec colonnes dynamiques basées sur l'état de la sidebar
 * - Transitions fluides lors des changements d'état
 * - Positionnement automatique du contenu sans marges manuelles
 * - Support responsive avec breakpoints
 * - Memoization pour optimiser les performances
 * 
 * Exigences: 4.1, 4.2, 5.1, 5.2, 5.3
 */
const AppLayoutComponent: React.FC<AppLayoutProps> = ({ 
  children, 
  showSidebar = true 
}) => {
  const { sidebarState } = useLayout();
  const [isBuyCreditModalOpen, setIsBuyCreditModalOpen] = useState(false);

  // Gestionnaire pour l'achat de crédits - ouvre la modal directement
  const handleBuyCredits = () => {
    setIsBuyCreditModalOpen(true);
  };
  
  return (
    <>
      <div 
        className="app-layout" 
        data-sidebar-visible={showSidebar}
        data-sidebar-state={sidebarState}
        data-testid="app-layout"
      >
        {showSidebar && <GlobalSidebar onBuyCredits={handleBuyCredits} />}
        {showSidebar && <CollapseButton />}
        <main className="main-content">
          {/* Bandeau d'avertissement pour solde faible - Requirement 6.3 */}
          <LowCreditBanner onBuyCredits={handleBuyCredits} />
          {children}
        </main>
      </div>
      
      {/* Modal d'achat de crédits */}
      <BuyCreditModal 
        open={isBuyCreditModalOpen} 
        onOpenChange={setIsBuyCreditModalOpen} 
      />
    </>
  );
};

// Memoization du composant pour éviter les re-renders inutiles
export const AppLayout = memo(AppLayoutComponent, (prevProps, nextProps) => {
  return (
    prevProps.showSidebar === nextProps.showSidebar &&
    prevProps.children === nextProps.children
  );
});